import {
    RuleConditionType,
    TransferRuleDirection,
} from "../../types/transactionRule";
import {
    IndexedCondition,
    IndexedRule,
    RuleScopeContext,
    SCOPE_TYPES,
    CATEGORY_TYPES,
    ARRAY_SET_TYPES,
    ROUNDING_TYPES,
    bucketKey,
    parseBucketKey,
    isSetSubset,
    isRoundingValue,
    isAmountValue,
    isDateValue,
    areRoundingValuesEqual,
    isSimilarToValueSubset,
} from "./types";
import {
    isAmountValueSubset,
    isDateValueSubset,
    isCategoryValueInHigherSet,
} from "./indexing";

/**
 * Same-type subset check. Called when both sides have at least one condition
 * of the same bucket key. Scope-family cross-type semantics are handled at a
 * higher level in `isScopeBucketsCovered`.
 */
export function isSameTypeConditionSubset(
    lower: IndexedCondition,
    higherSameType: readonly IndexedCondition[],
    _ctx: RuleScopeContext,
): boolean {
    const type = lower.condition.conditionType;

    if (type === RuleConditionType.ENTITY) {
        const lowerSet = lower.scopeEntities ?? new Set<number>();
        return higherSameType.every((h) =>
            isSetSubset(lowerSet, h.scopeEntities ?? new Set()),
        );
    }
    if (type === RuleConditionType.FINANCIAL_ACCOUNTS) {
        const lowerSet = lower.scopeAccounts ?? new Set<number>();
        return higherSameType.every((h) =>
            isSetSubset(lowerSet, h.scopeAccounts ?? new Set()),
        );
    }
    if (type === RuleConditionType.SUBACCOUNTS) {
        const lowerSet = lower.scopeSubaccounts ?? new Set<string>();
        return higherSameType.every((h) =>
            isSetSubset(lowerSet, h.scopeSubaccounts ?? new Set()),
        );
    }

    if (CATEGORY_TYPES.has(type)) {
        const lowerRaw = lower.categoryRaw ?? new Set<string>();
        if (lowerRaw.size === 0) {
            return false;
        }
        return higherSameType.every((h) => {
            const higherRaw = h.categoryRaw ?? new Set<string>();
            if (higherRaw.size === 0) {
                return false;
            }
            for (const lv of lowerRaw) {
                if (!isCategoryValueInHigherSet(lv, higherRaw)) {
                    return false;
                }
            }
            return true;
        });
    }

    if (type === RuleConditionType.SIMILAR_TO) {
        const l = lower.similarTo;
        if (!l) {
            return false;
        }
        return higherSameType.every(
            (h) =>
                h.similarTo !== undefined &&
                isSimilarToValueSubset(l, h.similarTo),
        );
    }

    if (ARRAY_SET_TYPES.has(type)) {
        const lowerSet = new Set(lower.values);
        return higherSameType.every((h) =>
            isSetSubset(lowerSet, new Set(h.values)),
        );
    }

    if (type === RuleConditionType.AMOUNT) {
        const l = lower.condition.value;
        if (!isAmountValue(l)) {
            return false;
        }
        return higherSameType.every((h) => {
            const r = h.condition.value;
            return isAmountValue(r) && isAmountValueSubset(l, r);
        });
    }

    if (type === RuleConditionType.DATE) {
        const l = lower.condition.value;
        if (!isDateValue(l)) {
            return false;
        }
        return higherSameType.every((h) => {
            const r = h.condition.value;
            return isDateValue(r) && isDateValueSubset(l, r);
        });
    }

    if (type === RuleConditionType.DATE_PROXIMITY) {
        const l = lower.condition.value;
        if (typeof l !== "number") {
            return false;
        }
        return higherSameType.every((h) => {
            const r = h.condition.value;
            return typeof r === "number" && r >= l;
        });
    }

    if (ROUNDING_TYPES.has(type)) {
        const l = lower.condition.value;
        if (!isRoundingValue(l)) {
            return false;
        }
        return higherSameType.every((h) => {
            if (!isRoundingValue(h.condition.value)) {
                return false;
            }
            return areRoundingValuesEqual(l, h.condition.value);
        });
    }

    return false;
}

function collectScopeConditions(
    rule: IndexedRule,
    direction: TransferRuleDirection | null,
): IndexedCondition[] {
    const entity = rule.buckets.get(
        bucketKey(RuleConditionType.ENTITY, direction),
    );
    const account = rule.buckets.get(
        bucketKey(RuleConditionType.FINANCIAL_ACCOUNTS, direction),
    );
    const subaccount = rule.buckets.get(
        bucketKey(RuleConditionType.SUBACCOUNTS, direction),
    );
    const all: IndexedCondition[] = [];
    if (entity) {
        all.push(...entity);
    }
    if (account) {
        all.push(...account);
    }
    if (subaccount) {
        all.push(...subaccount);
    }
    return all;
}

function isScopeLevelCovered<T>(
    higherConditions: readonly IndexedCondition[] | undefined,
    lowerScopeConditions: readonly IndexedCondition[],
    pickSet: (c: IndexedCondition) => ReadonlySet<T> | undefined,
): boolean {
    if (!higherConditions || higherConditions.length === 0) {
        return true;
    }
    for (const h of higherConditions) {
        const higherSet = pickSet(h) ?? new Set<T>();
        for (const l of lowerScopeConditions) {
            const implied = pickSet(l);
            if (!implied || implied.size === 0) {
                return false;
            }
            if (!isSetSubset(implied, higherSet)) {
                return false;
            }
        }
    }
    return true;
}

/**
 * For every scope condition on `higher` (in the given direction), the lower
 * rule must have a narrow-enough scope constraint that resolves to a subset
 * at the higher condition's level. See file header for the hierarchy.
 */
export function isScopeBucketsCovered(
    higher: IndexedRule,
    lower: IndexedRule,
    direction: TransferRuleDirection | null,
): boolean {
    const higherEntity = higher.buckets.get(
        bucketKey(RuleConditionType.ENTITY, direction),
    );
    const higherAccount = higher.buckets.get(
        bucketKey(RuleConditionType.FINANCIAL_ACCOUNTS, direction),
    );
    const higherSubaccount = higher.buckets.get(
        bucketKey(RuleConditionType.SUBACCOUNTS, direction),
    );

    if (!higherEntity && !higherAccount && !higherSubaccount) {
        return true;
    }

    const lowerScopeConditions = collectScopeConditions(lower, direction);
    if (lowerScopeConditions.length === 0) {
        return false;
    }

    return (
        isScopeLevelCovered(
            higherEntity,
            lowerScopeConditions,
            (c) => c.scopeEntities,
        ) &&
        isScopeLevelCovered(
            higherAccount,
            lowerScopeConditions,
            (c) => c.scopeAccounts,
        ) &&
        isScopeLevelCovered(
            higherSubaccount,
            lowerScopeConditions,
            (c) => c.scopeSubaccounts,
        )
    );
}

export function isRuleUnreachableAgainst(
    lower: IndexedRule,
    higher: IndexedRule,
    ctx: RuleScopeContext,
): boolean {
    for (const [key, higherConditions] of higher.buckets) {
        const parsed = parseBucketKey(key);
        if (!parsed) {
            continue;
        }
        if (SCOPE_TYPES.has(parsed.type)) {
            continue;
        }
        const lowerConditions = lower.buckets.get(key);
        if (!lowerConditions || lowerConditions.length === 0) {
            return false;
        }
        for (const l of lowerConditions) {
            if (!isSameTypeConditionSubset(l, higherConditions, ctx)) {
                return false;
            }
        }
    }

    const directionsWithScope = new Set<TransferRuleDirection | null>();
    for (const key of higher.buckets.keys()) {
        const parsed = parseBucketKey(key);
        if (!parsed || !SCOPE_TYPES.has(parsed.type)) {
            continue;
        }
        directionsWithScope.add(parsed.direction);
    }
    for (const direction of directionsWithScope) {
        if (!isScopeBucketsCovered(higher, lower, direction)) {
            return false;
        }
    }

    return true;
}
